'use client';
import { useState } from 'react';
import Calendar from 'react-calendar';
import type { CalendarEvent } from '../../types/home.types';
import {
	formatDateString,
	formatShortWeekday,
	getMonthNumber,
	hasEventOnDate,
	isDateInPeriod,
} from '../../utils/dateFormatter';

import styles from './HomeCalendar.module.css';
import 'react-calendar/dist/Calendar.css';

interface HomeCalendarProps {
	currentMonth: string;
	currentYear: number;
	events: CalendarEvent[];
	selectedPeriod?: {
		start: string;
		end: string;
	};
}

type ValuePiece = Date | null;
type Value = ValuePiece | [ValuePiece, ValuePiece];

export const HomeCalendar = ({
	currentMonth,
	currentYear,
	events,
	selectedPeriod,
}: HomeCalendarProps) => {
	const [value, setValue] = useState<Value>(new Date(currentYear, getMonthNumber(currentMonth), 1));

	const handleChange = (newValue: Value) => {
		setValue(newValue);
	};

	const tileClassName = ({ date }: { date: Date }) => {
		const dateStr = formatDateString(date);
		const classes: string[] = [];

		if (selectedPeriod) {
			const { start, end } = selectedPeriod;
			const periodCheck = isDateInPeriod(dateStr, start, end);

			if (periodCheck.isStart || periodCheck.isEnd) {
				classes.push(styles.periodEdge);
			} else if (periodCheck.isMiddle) {
				classes.push(styles.periodMiddle);
			}
		}

		if (hasEventOnDate(events, dateStr)) {
			classes.push(styles.hasEvent);
		}

		return classes.length > 0 ? classes.join(' ') : null;
	};

	return (
		<div className={styles.calendarContainer}>
			<div className={styles.calendarHeader}>
				<h3 className={styles.monthTitle}>{`${currentMonth} ${currentYear}`}</h3>
			</div>
			<Calendar
				value={value}
				onChange={handleChange}
				locale="pt-BR"
				tileClassName={tileClassName}
				className={styles.calendar}
				formatShortWeekday={formatShortWeekday}
			/>
		</div>
	);
};
